// L-curve helpers: turn GET /api/inversion/lcurve points (backend/inversion_lcurve.py) into
// chart rows. Misfit is on Y; X is the model norm when the models carry one (EarthImager),
// else the regularisation value (BERT lambda, EarthImager smoothness/damping).

export const X_AXES = {
  norm: { key: 'norm', label: 'Model L2-norm' },
  damping: { key: 'damping', label: 'Regularisation (damping / lambda)' },
};

const finite = (v) => typeof v === 'number' && Number.isFinite(v);

/** Which X axes have at least two plottable points; the first listed is the default. */
export function availableAxes(points) {
  const list = Array.isArray(points) ? points : [];
  return ['norm', 'damping'].filter((k) => list.filter((p) => p.plottable && finite(p[k])).length >= 2);
}

/** "smoothness 10" style label for a point (damping value with its kind when known). */
export function dampingLabel(p) {
  if (!finite(p?.damping)) return '';
  return p.damping_kind ? `${p.damping_kind} ${p.damping}` : `${p.damping}`;
}

/**
 * { rows, excluded, yKey, xKey }. Rows are sorted by X (the curve is drawn in that order);
 * `excluded` lists every requested model that cannot be placed, each with a reason.
 */
export function buildLcurveSeries(points, xKey) {
  const list = Array.isArray(points) ? points : [];
  const usable = list.filter((p) => p.plottable && finite(p[xKey]));
  const yKey = usable.length > 0 && usable.every((p) => finite(p.rms_percent)) ? 'rms_percent' : 'chi2';
  const rows = usable
    .filter((p) => finite(p[yKey]))
    .map((p) => ({ id: p.id, name: p.name, x: p[xKey], y: p[yKey], label: dampingLabel(p) || p.name || '' }))
    .sort((a, b) => a.x - b.x);
  const placed = new Set(rows.map((r) => r.id));
  const excluded = list
    .filter((p) => !placed.has(p.id))
    .map((p) => ({
      id: p.id,
      name: p.name,
      reason: p.reason || (finite(p[xKey]) ? 'No data misfit stored.' : `No ${X_AXES[xKey]?.label || xKey} stored for this model.`),
    }));
  return { rows, excluded, xKey, yKey };
}

export const Y_LABELS = { rms_percent: 'Final RMS (%)', chi2: 'Final chi²' };
